// covered-by: frontends/carrie/src/utils/parsers/__tests__/knowledge.test.ts
/**
 * Refreshed Knowledge Database types: thin re-exports from @mg/schemas (no
 * duplicated Zod — the drift between the old parsers/knowledge-db.ts copies
 * and the source schemas is exactly what this replaces) plus the pure
 * per-source status derivation the Sources tab renders.
 */
import {
  type KnowledgeRuleCitationResult,
  type KnowledgeSourceResult,
} from "@mg/schemas/src/christo/catalyst";
import {
  KnowledgeExtractionStatus,
  KnowledgeSourceStatus,
  KnowledgeTrustLevel,
} from "@mg/schemas/src/commons";

export {
  KnowledgeActivityAction,
  KnowledgeActivityActorKind,
  KnowledgeActivityTargetType,
  KnowledgeExtractionStatus,
  KnowledgeRuleCategory,
  KnowledgeRuleOrigin,
  KnowledgeRuleStatus,
  KnowledgeScopeSource,
  KnowledgeSourceInteraction,
  KnowledgeSourceStatus,
  KnowledgeSourceType,
  KnowledgeTrustLevel,
} from "@mg/schemas/src/commons";
export {
  getKnowledgeActivityResponseSchema,
  getKnowledgeRulesResponseSchema,
  getKnowledgeSourceDownloadResponseSchema,
  getKnowledgeSourcesResponseSchema,
  knowledgeRuleResultSchema,
  knowledgeSourceResultSchema,
  type GetKnowledgeActivityResponse,
  type GetKnowledgeRulesResponse,
  type GetKnowledgeSourceDownloadResponse,
  type GetKnowledgeSourcesResponse,
  type KnowledgeActivityResult,
  type KnowledgeRuleCitationResult,
  type KnowledgeRuleResult,
  type KnowledgeSourceResult,
  type UpdateKnowledgeScopeBody,
} from "@mg/schemas/src/christo/catalyst";

/**
 * The single user-facing lifecycle status per source. Storage keeps the
 * samba-owned ingest `status` and the rembrandt-owned `extraction` subdoc
 * separate; the UI collapses them into ONE chip:
 *
 *   Uploaded -> Processing -> Extracting rules -> Ready · N rules
 *                                   \-> Extraction failed (admin Retry)
 *          \-> Failed (ingest)
 */
export type DerivedSourceStatus =
  | { kind: "uploaded"; label: string }
  | { kind: "processing"; label: string }
  | { kind: "extracting"; label: string }
  | { kind: "ready"; label: string; ruleCount: number }
  | { kind: "failed"; label: string; error: string | null }
  | { kind: "extraction_failed"; label: string; error: string | null };

export function deriveSourceStatus(
  source: Pick<KnowledgeSourceResult, "status" | "error" | "extraction">,
): DerivedSourceStatus {
  if (source.status === KnowledgeSourceStatus.FAILED) {
    return { kind: "failed", label: "Failed", error: source.error ?? null };
  }
  if (source.status === KnowledgeSourceStatus.UPLOADED) {
    return { kind: "uploaded", label: "Uploaded" };
  }
  if (source.status === KnowledgeSourceStatus.PROCESSING) {
    return { kind: "processing", label: "Processing" };
  }

  // Ingest reached ready — the extraction subdoc drives the rest.
  const extraction = source.extraction;
  if (extraction == null) {
    return { kind: "processing", label: "Processing" };
  }
  switch (extraction.status) {
    case KnowledgeExtractionStatus.FAILED:
      return {
        kind: "extraction_failed",
        label: "Extraction failed",
        error: extraction.error ?? null,
      };
    case KnowledgeExtractionStatus.SUCCEEDED: {
      const ruleCount = extraction.ruleCount ?? 0;
      return {
        kind: "ready",
        label: `Ready · ${ruleCount} ${ruleCount === 1 ? "rule" : "rules"}`,
        ruleCount,
      };
    }
    case KnowledgeExtractionStatus.PENDING:
    case KnowledgeExtractionStatus.RUNNING:
    default:
      return { kind: "extracting", label: "Extracting rules" };
  }
}

/** True while the sources list should poll (ingest or extraction in flight). */
export function isSourceInFlight(
  source: Pick<KnowledgeSourceResult, "status" | "error" | "extraction">,
): boolean {
  const derived = deriveSourceStatus(source);
  return (
    derived.kind === "uploaded" ||
    derived.kind === "processing" ||
    derived.kind === "extracting"
  );
}

/**
 * Mirror of rembrandt's ``sort_citations_primary_first`` (anchoring.py) so
 * rules synthesized under the OLD ordering still display correctly: high
 * trust before low; within a trust tier the most recently extracted SOURCE
 * wins (a source's recency is its newest citation's extractedAt); within one
 * source the citation most relevant to the rule (similarity) leads, then
 * extraction confidence. Stable — legacy citations without the ranking
 * fields keep their stored order at the back of their trust tier.
 */
export function orderCitationsPrimaryFirst(
  citations: KnowledgeRuleCitationResult[],
): KnowledgeRuleCitationResult[] {
  const trustRank = (c: KnowledgeRuleCitationResult) =>
    c.trustLevel === KnowledgeTrustLevel.HIGH ? 0 : 1;
  const extractedTs = (c: KnowledgeRuleCitationResult) => {
    if (c.extractedAt == null) return 0;
    const ts = new Date(c.extractedAt).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };
  const sourceRecency = new Map<string, number>();
  for (const c of citations) {
    const ts = extractedTs(c);
    if (ts > (sourceRecency.get(c.sourceId) ?? 0)) {
      sourceRecency.set(c.sourceId, ts);
    }
  }
  return [...citations].sort(
    (a, b) =>
      trustRank(a) - trustRank(b) ||
      (sourceRecency.get(b.sourceId) ?? 0) -
        (sourceRecency.get(a.sourceId) ?? 0) ||
      (b.similarity ?? 0) - (a.similarity ?? 0) ||
      (b.confidence ?? 0) - (a.confidence ?? 0),
  );
}
